/** Save text as a file, and build CSV that Excel opens correctly.
 *
 *  THE ONE IMPLEMENTATION. There were two before this, written independently:
 *  `modules/connect/pages.tsx` and `modules/registry/DevicesPage.tsx`, and
 *  they disagreed on the part that matters. The connect one appended the
 *  anchor to the document and revoked the object URL after 500 ms; the
 *  registry one did neither, which leaks the blob for the life of the tab and
 *  fails outright in browsers that refuse a click on an unattached element.
 *  Adding a third for the history download is what prompted the extraction.
 *
 *  Dattatray: *"today there is no way to download that data -- we can give a
 *  download button to download that data in Excel format."*
 *
 *  IT WRITES CSV, NOT .xlsx, AND THAT IS A CHOICE. Real xlsx is a zip of XML
 *  parts and needs a library (SheetJS is ~500 KB) to produce -- a lot of
 *  bundle for a save button. CSV opens natively in Excel, and the UTF-8 BOM
 *  below is what makes it open CORRECTLY: without it Excel reads the file as
 *  the system codepage and mangles every non-ASCII character. If a real
 *  workbook is ever needed (multiple sheets, formats, formulas), that is the
 *  point to add the dependency, not before.
 */

/** A string Excel will read as a NUMBER: optional sign, digits, optional
 *  decimal part, optional exponent. Nothing else — no thousands separators,
 *  no currency, no padding. */
const NUMERIC = /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/;

/** Quote a value for CSV: double the quotes, wrap when it could break a row. */
function cell(v: unknown): string {
  if (v === null || v === undefined) return "";
  // A real number never needs escaping and must never BE escaped. Taken first,
  // so the sigil test below never sees its minus sign.
  if (typeof v === "number") return Number.isFinite(v) ? String(v) : "";
  const s = String(v);
  // A leading =, +, -, @, TAB or CR makes Excel treat the cell as a FORMULA,
  // which is how a CSV export becomes an injection vector. Prefix with a
  // quote so it stays text; the quote is not shown once Excel parses it.
  //
  // BUT A NEGATIVE NUMBER IS NOT A FORMULA, and treating it as one was worse
  // than cosmetic. Dattatray: *"In Histric CVS downalod the neagtive values
  // start with ' character."* `'-12.5` is TEXT to Excel, so a column of pack
  // currents — negative for every amp of regen — could not be summed, charted
  // or compared. The export quietly stopped being data for exactly the rows
  // somebody downloads it to look at. Numbers now pass through untouched; the
  // guard still fires on `-A1+B1` and `=cmd|…`, which is the actual attack.
  const safe = !NUMERIC.test(s) && /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
  return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

/** Rows of objects -> CSV text. `headers` fixes the column order; anything not
 *  listed is appended in first-seen order, so a new signal still exports. */
export function toCsv(rows: Record<string, unknown>[], headers?: string[]): string {
  const seen = headers ? [...headers] : [];
  for (const r of rows) for (const k of Object.keys(r)) if (!seen.includes(k)) seen.push(k);
  const head = seen.map(cell).join(",");
  const body = rows.map((r) => seen.map((k) => cell(r[k])).join(","));
  return [head, ...body].join("\r\n");          // CRLF: what Excel expects
}

/** Save `text` as `name`. Attaches the anchor before clicking (some browsers
 *  refuse a detached one) and revokes the URL afterwards. */
export function download(name: string, text: string, type = "text/csv;charset=utf-8") {
  // U+FEFF so Excel reads UTF-8 instead of the local codepage.
  const blob = new Blob([type.startsWith("text/csv") ? "﻿" + text : text], { type });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 500);
}

/** The common case: objects straight to a saved spreadsheet. */
export function downloadCsv(name: string, rows: Record<string, unknown>[],
                            headers?: string[]): void {
  download(name, toCsv(rows, headers));
}
